import { Injectable, isDevMode } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ContentPaginator, WebPost, WebGalleryCard, GALLERY_SMALL_INDI } from '../models/models';

import { pageItemsPhoto        } from '../models/page-photo';
import { pageItemsPainting     } from '../models/page-painting';
import { pageItemsInstallation } from '../models/page-installation';
import { pageItemsExhibition   } from '../models/page-exhibition';
import * as Texts from '../models/demo-texty';

export interface PostLocation {
    landingPath: string;  // route of the content page which contains the post
    postId: string;
}

const SECTIONS: Record<string, WebPost[]> = {
    'photo'       : pageItemsPhoto,
    'painting'    : pageItemsPainting,
    'installation': pageItemsInstallation,
    'exhibition'  : pageItemsExhibition,
    /* TODO - remove this - only for DEVEL */
    'demo'        : Texts.pageItemsDEMO,
};

@Injectable({
    providedIn: 'root'
})
export class ContentApiService {

    items : WebPost[] = [];

    constructor() {
        if (isDevMode()) {
            this.checkUniquePostIds();
        }
    }

    private checkUniquePostIds() {
        const seen = new Map<string, string>();
        for (const [section, posts] of Object.entries(SECTIONS)) {
            for (const post of posts) {
                if (!post.id) {
                    console.error(`ContentApiService: post without id in section '${section}'`, post);
                } else if (seen.has(post.id)) {
                    console.error(`ContentApiService: duplicate post id '${post.id}' in sections '${seen.get(post.id)}' and '${section}'`);
                } else {
                    seen.set(post.id, section);
                }
            }
        }
    }

    private adjustPost(post: WebPost) {
            if (post.type == 'gallery') {
                const gal = post.content as WebGalleryCard;
                if (gal) {
                    if (gal.items.length>30 || gal.text == 'pohadka!') {
                    // moc dlouha galerie - zmenit na photo
                        post.type = 'photo';
                        post.content = {
                            title: 'Fotka',
                            description: "<p><b>Příliš dlouhá galerka - tak si raději povíme pohádku</b>, abysme nerozbili layout</p>" + Texts.textPohadka,
                            src: 'https://www.infracz.cz/images/thumbs/0026711_pohadka-z-kufriku-o-neposlusnych-kuzlatkach-kuzlatka_01_600.jpeg',
                        }
                    }
                    else if (gal.items.length>13) {
                        gal.style = (gal.style ? gal.style : 0) | GALLERY_SMALL_INDI;
                    }
                }
            }
    }

    // returns items of pages fromPage..page (inclusive)
    private processDummyResponse(page: number, itemsPerPage: number, fromPage: number = page) : ContentPaginator {
        const items: WebPost[] = [];
        let index: number = (fromPage - 1) * itemsPerPage;
        const endIndex = page * itemsPerPage;
        while (index < endIndex && index < this.items.length) {
            const post = this.items[index] as WebPost;
            this.adjustPost(post);
            items.push(post);
            index++;
        }
        let container = {
            // items: (page % 3 < 2) ? [...this.items] : [...this.items],
            items: items,
            page: page,
            hasMorePages: index < this.items.length
            /* response.skip + response.limit < response.total */
        };

        return container as ContentPaginator;
    }

    selectSection(section: string) {
        this.items = SECTIONS[section] ?? [];
    }

    // TODO - replace with HTTP request (e.g. GET /api/post/<id>/location) once content is loaded dynamically
    public locatePost$(postId: string): Observable<PostLocation | null> {
        for (const [section, posts] of Object.entries(SECTIONS)) {
            if (posts.some(p => p.id === postId)) {
                return of({ landingPath: '/' + section, postId });
            }
        }
        return of(null);
    }

    // Initial load of the selected section which includes the given post; continue paging with getProducts$(result.page + 1).
    // Falls back to the first page when the post is not in the section.
    public getProductsUntil$(postId: string, itemsPerPage: number = 5): Observable<ContentPaginator> {
        const index = this.items.findIndex(p => p.id === postId);
        if (index < 0) {
            return this.getProducts$(1, itemsPerPage);
        }
        const page = Math.floor(index / itemsPerPage) + 1;
        return of(this.processDummyResponse(page, itemsPerPage, 1));
    }

    public getProducts$(page: number = 1, itemsPerPage: number = 5): Observable<ContentPaginator> {
        let observable = new Observable<ContentPaginator>(observer => {
            observer.next(this.processDummyResponse(page, itemsPerPage));
        });
        return observable;
    }
}
